import { useEffect, useState } from 'react';
import { adminGet, adminSend, centsToDollars, dollarsToCents } from '../../api/adminClient';
import { useAdminView } from '../../context/AdminViewContext';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n/useI18n';
import { followLink, pathForSection } from '../../router/location';
import styles from './admin.module.css';

const SECTIONS = ['overview', 'users', 'plans', 'subscriptions', 'transactions'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'SAR'];
const SUB_STATUSES = ['trialing', 'active', 'past_due', 'canceled', 'incomplete', 'paused'];
const TX_STATUSES = ['pending', 'succeeded', 'failed', 'refunded'];

const EMPTY_PLAN = {
  id: '',
  name: '',
  description: '',
  price: '',
  currency: 'USD',
  interval: 'month',
  active: true,
  stripePriceId: '',
};

function money(cents, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents) / 100);
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
  }
}

function when(value, lang) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : 'en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

function tone(status) {
  if (status === 'active' || status === 'succeeded' || status === 'trialing' || status === 'processed') return 'good';
  if (status === 'past_due' || status === 'pending' || status === 'paused' || status === 'ignored') return 'wait';
  if (status === 'failed' || status === 'canceled' || status === 'refunded') return 'bad';
  return '';
}

function Pill({ status, t }) {
  return (
    <span className={styles.pill} data-tone={tone(status)}>
      {t(`admin.status.${status}`)}
    </span>
  );
}

export function AdminDashboard() {
  const { closeAdmin, goSection, section } = useAdminView();
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showProPlans, setShowProPlans] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([
      adminGet('/summary'),
      adminGet('/plans'),
      adminGet('/subscriptions'),
      adminGet('/stripe'),
      adminGet('/settings'),
    ]).then(([summaryResult, planResult, subResult, stripeResult, settingsResult]) => {
      if (cancel) return;
      if (!summaryResult.ok || !planResult.ok || !subResult.ok) {
        setError(summaryResult.status === 403 ? 'forbidden' : 'load');
        setLoading(false);
        return;
      }
      setError('');
      setSummary(summaryResult.body);
      setPlans(planResult.body.plans || []);
      if (settingsResult.ok) setShowProPlans(settingsResult.body.showProPlans !== false);
      setSubscriptions(subResult.body.subscriptions || []);
      setStripe(stripeResult.ok ? stripeResult.body : null);
      setLoading(false);
    });
    return () => {
      cancel = true;
    };
  }, [reload]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') closeAdmin();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeAdmin]);

  const refresh = () => setReload((value) => value + 1);

  const saveError = (result) => {
    if (result.body?.error === 'user_not_found') setNotice('user');
    else setNotice('save');
  };

  return (
    <div className={styles.admin}>
      <aside className={styles.rail}>
        <div className={styles.brand}>
          <strong>ZIPPAX</strong>
          <span>{t('admin.kicker')}</span>
        </div>
        <nav className={styles.nav} aria-label={t('account.admin')}>
          {SECTIONS.map((id) => {
            const path = pathForSection(id);
            return (
              <a
                key={id}
                href={path}
                aria-current={section === id ? 'page' : undefined}
                onClick={(event) => followLink(event, () => goSection(id), path)}
              >
                {t(`admin.${id}`)}
              </a>
            );
          })}
        </nav>
        <a href="/" className={styles.back} onClick={(event) => followLink(event, closeAdmin, '/')}>
          {t('admin.back')}
        </a>
      </aside>
      <main className={styles.main}>
        <header className={styles.head}>
          <div>
            <h1>{t(`admin.${section}`)}</h1>
            {section !== 'users' ? (
              <>
                <p>
                  {stripe?.configured
                    ? t(stripe.mode === 'live' ? 'admin.stripeLive' : 'admin.stripeTest')
                    : t('admin.stripe')}
                </p>
                {stripe?.configured && !stripe.webhook ? <p>{t('admin.webhookOff')}</p> : null}
              </>
            ) : null}
          </div>
        </header>
        {loading ? <p className={styles.empty}>{t('admin.loading')}</p> : null}
        {error === 'forbidden' ? <p className={styles.banner}>{t('admin.forbidden')}</p> : null}
        {error === 'load' ? <p className={styles.banner}>{t('admin.loadError')}</p> : null}
        {notice ? (
          <p className={styles.banner} role="alert">
            {notice === 'user' ? t('admin.userMissing') : t('admin.saveError')}
          </p>
        ) : null}
        {!loading && !error && section === 'overview' && (
          <Overview summary={summary} t={t} lang={lang} />
        )}
        {!loading && !error && section === 'users' && (
          <Users t={t} lang={lang} selfId={user?.id || ''} />
        )}
        {!loading && !error && section === 'plans' && (
          <Plans
            plans={plans}
            showProPlans={showProPlans}
            onShowProPlans={setShowProPlans}
            t={t}
            onSaved={refresh}
            onError={saveError}
            clearNotice={() => setNotice('')}
          />
        )}
        {!loading && !error && section === 'subscriptions' && (
          <Subscriptions
            rows={subscriptions}
            plans={plans}
            t={t}
            lang={lang}
            onSaved={refresh}
            onError={saveError}
            clearNotice={() => setNotice('')}
          />
        )}
        {!loading && !error && section === 'transactions' && (
          <Transactions t={t} lang={lang} />
        )}
      </main>
    </div>
  );
}

function Overview({ summary, t, lang }) {
  const cards = [
    [t('admin.mrr'), money(summary?.mrrCents || 0, 'USD')],
    [t('admin.activeSubs'), String(summary?.activeSubscriptions || 0)],
    [t('admin.collected'), money(summary?.revenueCents || 0, 'USD'), t('admin.paidCount', { n: summary?.paidCount || 0 })],
    [t('admin.activePlans'), String(summary?.activePlans || 0)],
  ];
  return (
    <>
      <section className={styles.stats}>
        {cards.map(([label, value, extra]) => (
          <article key={label} className={styles.stat}>
            <span>{label}</span>
            <strong>{value}</strong>
            {extra ? <em>{extra}</em> : null}
          </article>
        ))}
      </section>
      <section className={styles.panel}>
        <h2>{t('admin.recent')}</h2>
        {summary?.recentTransactions?.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.when')}</th>
                <th>{t('admin.customer')}</th>
                <th>{t('admin.note')}</th>
                <th>{t('admin.amount')}</th>
                <th>{t('admin.status')}</th>
              </tr>
            </thead>
            <tbody>
              {summary.recentTransactions.map((row) => (
                <tr key={row.id}>
                  <td>{when(row.createdAt, lang)}</td>
                  <td>{row.email || '—'}</td>
                  <td>{row.description || '—'}</td>
                  <td>{money(row.amountCents, row.currency)}</td>
                  <td><Pill status={row.status} t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className={styles.empty}>{t('admin.empty')}</p>
        )}
      </section>
    </>
  );
}

function Users({ t, lang, selfId }) {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');
  const [pendingId, setPendingId] = useState('');
  const [confirmId, setConfirmId] = useState('');

  useEffect(() => {
    let cancel = false;
    adminGet('/users').then((result) => {
      if (cancel || !result.ok) return;
      setRows(result.body.users || []);
    });
    return () => {
      cancel = true;
    };
  }, []);

  const shown = rows.filter((row) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return row.email.toLowerCase().includes(needle) || row.name.toLowerCase().includes(needle);
  });

  const changeRole = async (row, role) => {
    if (role === row.role) return;
    setNotice('');
    setPendingId(row.id);
    const result = await adminSend(`/users/${row.id}`, 'PATCH', { role });
    setPendingId('');
    if (!result.ok) {
      setNotice(result.body?.error === 'last_admin' ? 'last' : 'save');
      return;
    }
    setRows((current) => current.map((item) => (
      item.id === row.id ? { ...item, role: result.body.user.role } : item
    )));
  };

  const remove = async (row) => {
    setNotice('');
    setPendingId(row.id);
    const result = await adminSend(`/users/${row.id}`, 'DELETE', {});
    setPendingId('');
    if (!result.ok) {
      setNotice(result.body?.error === 'last_admin' ? 'last' : 'save');
      return;
    }
    setConfirmId('');
    setRows((current) => current.filter((item) => item.id !== row.id));
  };

  return (
    <>
      <div className={styles.toolbar}>
        <label className={styles.userSearch}>
          {t('admin.search')}
          <input value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <p className={styles.count}>{t('admin.matches', { n: shown.length })}</p>
      </div>
      {notice ? (
        <p className={styles.banner} role="alert">
          {notice === 'last' ? t('admin.lastAdmin') : t('admin.saveError')}
        </p>
      ) : null}
      <section className={styles.panel}>
        {shown.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.name')}</th>
                <th>{t('admin.customer')}</th>
                <th>{t('admin.role')}</th>
                <th>{t('admin.plan')}</th>
                <th>{t('admin.joined')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const mine = row.id === selfId;
                return (
                  <tr key={row.id}>
                    <td>
                      {row.name || '—'}
                      {mine ? <div className={styles.muted}>{t('admin.you')}</div> : null}
                    </td>
                    <td>{row.email}</td>
                    <td>
                      <select
                        className={styles.select}
                        value={row.role}
                        aria-label={t('admin.role')}
                        disabled={mine || pendingId === row.id}
                        onChange={(event) => changeRole(row, event.target.value)}
                      >
                        <option value="user">{t('admin.role.user')}</option>
                        <option value="admin">{t('admin.role.admin')}</option>
                      </select>
                    </td>
                    <td>
                      {row.planName ? (
                        <>
                          {row.planName}
                          {row.subscriptionStatus ? (
                            <div><Pill status={row.subscriptionStatus} t={t} /></div>
                          ) : null}
                        </>
                      ) : (
                        <span className={styles.muted}>{t('admin.noPlan')}</span>
                      )}
                    </td>
                    <td>{when(row.createdAt, lang)}</td>
                    <td>
                      {mine ? null : confirmId === row.id ? (
                        <div className={styles.rowActions}>
                          <button type="button" className={styles.tiny} disabled={pendingId === row.id} onClick={() => remove(row)}>
                            {t('admin.confirmDelete')}
                          </button>
                          <button type="button" className={styles.tiny} onClick={() => setConfirmId('')}>
                            {t('admin.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button type="button" className={styles.tiny} onClick={() => setConfirmId(row.id)}>
                          {t('admin.deleteUser')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className={styles.empty}>{query.trim() ? t('admin.noMatches') : t('admin.empty')}</p>
        )}
      </section>
    </>
  );
}

function Plans({ plans, showProPlans, onShowProPlans, t, onSaved, onError, clearNotice }) {
  const [draft, setDraft] = useState(null);
  const [pending, setPending] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);

  const togglePlans = async (event) => {
    const next = event.target.checked;
    clearNotice();
    setSavingVisibility(true);
    const result = await adminSend('/settings', 'PATCH', { showProPlans: next });
    setSavingVisibility(false);
    if (!result.ok) {
      onError(result);
      return;
    }
    onShowProPlans(result.body.showProPlans !== false);
  };

  const submit = async (event) => {
    event.preventDefault();
    const amountCents = dollarsToCents(draft.price);
    if (amountCents == null || !draft.name.trim()) {
      onError({ body: {} });
      return;
    }
    clearNotice();
    setPending(true);
    const body = {
      name: draft.name,
      description: draft.description,
      amountCents,
      currency: draft.currency,
      interval: draft.interval,
      active: draft.active,
      stripePriceId: draft.stripePriceId,
    };
    const result = draft.id
      ? await adminSend(`/plans/${draft.id}`, 'PATCH', body)
      : await adminSend('/plans', 'POST', body);
    setPending(false);
    if (!result.ok) {
      onError(result);
      return;
    }
    setDraft(null);
    onSaved();
  };

  return (
    <>
      <div className={styles.toolbar}>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={showProPlans}
            disabled={savingVisibility}
            onChange={togglePlans}
          />
          {t('admin.showProPlans')}
        </label>
        <button type="button" className={styles.primary} onClick={() => { clearNotice(); setDraft({ ...EMPTY_PLAN }); }}>
          {t('admin.newPlan')}
        </button>
      </div>
      {draft && (
        <form className={styles.form} onSubmit={submit}>
          <label className={styles.wide}>
            {t('admin.name')}
            <input value={draft.name} maxLength={80} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required />
          </label>
          <label className={styles.wide}>
            {t('admin.description')}
            <textarea value={draft.description} maxLength={500} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
          </label>
          <label>
            {t('admin.price')}
            <input inputMode="decimal" value={draft.price} onChange={(event) => setDraft({ ...draft, price: event.target.value })} required />
          </label>
          <label>
            {t('admin.currency')}
            <select value={draft.currency} onChange={(event) => setDraft({ ...draft, currency: event.target.value })}>
              {CURRENCIES.map((currency) => <option key={currency}>{currency}</option>)}
            </select>
          </label>
          <label>
            {t('admin.interval')}
            <select value={draft.interval} onChange={(event) => setDraft({ ...draft, interval: event.target.value })}>
              <option value="month">{t('admin.month')}</option>
              <option value="year">{t('admin.year')}</option>
            </select>
          </label>
          <label className={styles.check}>
            <input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} />
            {t('admin.active')}
          </label>
          <label className={styles.wide}>
            {t('admin.stripePrice')}
            <input value={draft.stripePriceId} maxLength={80} placeholder={t('admin.optional')} onChange={(event) => setDraft({ ...draft, stripePriceId: event.target.value })} />
          </label>
          <div className={styles.actions}>
            <button type="button" className={styles.ghost} onClick={() => setDraft(null)}>{t('admin.cancel')}</button>
            <button type="submit" className={styles.primary} disabled={pending}>{pending ? t('admin.saving') : t('admin.save')}</button>
          </div>
        </form>
      )}
      <section className={styles.panel}>
        {plans.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.name')}</th>
                <th>{t('admin.price')}</th>
                <th>{t('admin.interval')}</th>
                <th>{t('admin.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    {plan.name}
                    {plan.description ? <div className={styles.muted}>{plan.description}</div> : null}
                  </td>
                  <td>{money(plan.amountCents, plan.currency)}</td>
                  <td>{plan.interval === 'year' ? t('admin.year') : t('admin.month')}</td>
                  <td>
                    {plan.active ? <Pill status="active" t={t} /> : <span className={styles.pill}>{t('admin.inactive')}</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.tiny}
                      onClick={() => setDraft({
                        id: plan.id,
                        name: plan.name,
                        description: plan.description || '',
                        price: centsToDollars(plan.amountCents),
                        currency: plan.currency,
                        interval: plan.interval,
                        active: plan.active,
                        stripePriceId: plan.stripePriceId || '',
                      })}
                    >
                      {t('admin.editPlan')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className={styles.empty}>{t('admin.empty')}</p>
        )}
      </section>
    </>
  );
}

function Subscriptions({ rows, plans, t, lang, onSaved, onError, clearNotice }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [planId, setPlanId] = useState(plans[0]?.id || '');
  const [status, setStatus] = useState('active');
  const [periodEnd, setPeriodEnd] = useState('');
  const [pending, setPending] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    clearNotice();
    setPending(true);
    const result = await adminSend('/subscriptions', 'POST', {
      email,
      planId,
      status,
      currentPeriodEnd: periodEnd || null,
      cancelAtPeriodEnd: false,
    });
    setPending(false);
    if (!result.ok) {
      onError(result);
      return;
    }
    setOpen(false);
    setEmail('');
    onSaved();
  };

  const changeStatus = async (id, next) => {
    clearNotice();
    const result = await adminSend(`/subscriptions/${id}`, 'PATCH', { status: next });
    if (!result.ok) onError(result);
    else onSaved();
  };

  return (
    <>
      <div className={styles.toolbar}>
        <button type="button" className={styles.primary} onClick={() => setOpen(true)}>{t('admin.newSub')}</button>
      </div>
      {open && (
        <form className={styles.form} onSubmit={submit}>
          <label className={styles.wide}>
            {t('admin.customer')}
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label>
            {t('admin.plan')}
            <select value={planId} onChange={(event) => setPlanId(event.target.value)} required>
              {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
            </select>
          </label>
          <label>
            {t('admin.status')}
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {SUB_STATUSES.map((item) => <option key={item} value={item}>{t(`admin.status.${item}`)}</option>)}
            </select>
          </label>
          <label>
            {t('admin.periodEnd')}
            <input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
          </label>
          <div className={styles.actions}>
            <button type="button" className={styles.ghost} onClick={() => setOpen(false)}>{t('admin.cancel')}</button>
            <button type="submit" className={styles.primary} disabled={pending || !plans.length}>{pending ? t('admin.saving') : t('admin.save')}</button>
          </div>
        </form>
      )}
      <section className={styles.panel}>
        {rows.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.customer')}</th>
                <th>{t('admin.plan')}</th>
                <th>{t('admin.periodEnd')}</th>
                <th>{t('admin.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.email}</td>
                  <td>{row.planName}<div className={styles.muted}>{money(row.amountCents, row.currency)}</div></td>
                  <td>{when(row.currentPeriodEnd, lang)}</td>
                  <td>
                    <select className={styles.select} value={row.status} aria-label={t('admin.status')} onChange={(event) => changeStatus(row.id, event.target.value)}>
                      {SUB_STATUSES.map((item) => <option key={item} value={item}>{t(`admin.status.${item}`)}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className={styles.empty}>{t('admin.empty')}</p>
        )}
      </section>
    </>
  );
}

const EMPTY_TX_FILTERS = {
  q: '',
  status: '',
  currency: '',
  from: '',
  to: '',
  min: '',
  max: '',
};

const EMPTY_EVENT_FILTERS = {
  q: '',
  status: '',
  type: '',
  from: '',
  to: '',
};

function filterQuery(filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    const text = String(value ?? '').trim();
    if (!text) return;
    if (key === 'min' || key === 'max') {
      const cents = dollarsToCents(text);
      if (cents == null) return;
      params.set(key, String(cents));
      return;
    }
    params.set(key, text);
  });
  return params;
}

function Transactions({ t, lang }) {
  const [view, setView] = useState('payments');
  const [draft, setDraft] = useState(EMPTY_TX_FILTERS);
  const [eventDraft, setEventDraft] = useState(EMPTY_EVENT_FILTERS);
  const [applied, setApplied] = useState(EMPTY_TX_FILTERS);
  const [appliedEvents, setAppliedEvents] = useState(EMPTY_EVENT_FILTERS);
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [eventTotal, setEventTotal] = useState(0);
  const [busy, setBusy] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [status, setStatus] = useState('succeeded');
  const [description, setDescription] = useState('');
  const [pending, setPending] = useState(false);
  const [formError, setFormError] = useState('');

  const loadPayments = async (filters, offset, append) => {
    const params = filterQuery(filters);
    params.set('limit', '50');
    params.set('offset', String(offset));
    setBusy(true);
    const result = await adminGet(`/transactions?${params.toString()}`);
    setBusy(false);
    if (!result.ok) {
      setFailed(true);
      return;
    }
    setFailed(false);
    const next = result.body.transactions || [];
    setRows((current) => (append ? current.concat(next) : next));
    setTotal(result.body.total || 0);
  };

  const loadEvents = async (filters, offset, append) => {
    const params = filterQuery(filters);
    params.set('limit', '50');
    params.set('offset', String(offset));
    setBusy(true);
    const result = await adminGet(`/events?${params.toString()}`);
    setBusy(false);
    if (!result.ok) {
      setFailed(true);
      return;
    }
    setFailed(false);
    const next = result.body.events || [];
    setEvents((current) => (append ? current.concat(next) : next));
    setEventTotal(result.body.total || 0);
  };

  useEffect(() => {
    loadPayments(EMPTY_TX_FILTERS, 0, false);
  }, []);

  const applyPayments = (event) => {
    event.preventDefault();
    setApplied(draft);
    loadPayments(draft, 0, false);
  };

  const applyEvents = (event) => {
    event.preventDefault();
    setAppliedEvents(eventDraft);
    loadEvents(eventDraft, 0, false);
  };

  const submit = async (event) => {
    event.preventDefault();
    const amountCents = dollarsToCents(price);
    if (amountCents == null) {
      setFormError('save');
      return;
    }
    setFormError('');
    setPending(true);
    const result = await adminSend('/transactions', 'POST', {
      email,
      amountCents,
      currency,
      status,
      description,
    });
    setPending(false);
    if (!result.ok) {
      setFormError(result.body?.error === 'user_not_found' ? 'user' : 'save');
      return;
    }
    setOpen(false);
    setEmail('');
    setPrice('');
    setDescription('');
    loadPayments(applied, 0, false);
  };

  const shown = view === 'payments' ? rows.length : events.length;
  const shownTotal = view === 'payments' ? total : eventTotal;

  return (
    <>
      <div className={styles.switcher} role="tablist" aria-label={t('admin.transactions')}>
        <button type="button" aria-selected={view === 'payments'} onClick={() => setView('payments')}>
          {t('admin.payments')}
        </button>
        <button
          type="button"
          aria-selected={view === 'events'}
          onClick={() => {
            setView('events');
            if (!events.length) loadEvents(appliedEvents, 0, false);
          }}
        >
          {t('admin.events')}
        </button>
      </div>
      {view === 'payments' ? (
        <form className={styles.filters} onSubmit={applyPayments}>
          <label className={styles.grow}>
            {t('admin.search')}
            <input value={draft.q} onChange={(event) => setDraft({ ...draft, q: event.target.value })} />
          </label>
          <label>
            {t('admin.status')}
            <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>
              <option value="">{t('admin.any')}</option>
              {TX_STATUSES.map((item) => <option key={item} value={item}>{t(`admin.status.${item}`)}</option>)}
            </select>
          </label>
          <label>
            {t('admin.currency')}
            <select value={draft.currency} onChange={(event) => setDraft({ ...draft, currency: event.target.value })}>
              <option value="">{t('admin.any')}</option>
              {CURRENCIES.map((item) => <option key={item}>{item}</option>)}
            </select>
          </label>
          <label>
            {t('admin.from')}
            <input type="date" value={draft.from} onChange={(event) => setDraft({ ...draft, from: event.target.value })} />
          </label>
          <label>
            {t('admin.to')}
            <input type="date" value={draft.to} onChange={(event) => setDraft({ ...draft, to: event.target.value })} />
          </label>
          <label>
            {t('admin.min')}
            <input inputMode="decimal" value={draft.min} onChange={(event) => setDraft({ ...draft, min: event.target.value })} />
          </label>
          <label>
            {t('admin.max')}
            <input inputMode="decimal" value={draft.max} onChange={(event) => setDraft({ ...draft, max: event.target.value })} />
          </label>
          <div className={styles.filterActions}>
            <button type="submit" className={styles.primary}>{t('admin.apply')}</button>
            <button
              type="button"
              className={styles.ghost}
              onClick={() => {
                setDraft(EMPTY_TX_FILTERS);
                setApplied(EMPTY_TX_FILTERS);
                loadPayments(EMPTY_TX_FILTERS, 0, false);
              }}
            >
              {t('admin.clear')}
            </button>
          </div>
        </form>
      ) : (
        <form className={styles.filters} onSubmit={applyEvents}>
          <label className={styles.grow}>
            {t('admin.search')}
            <input value={eventDraft.q} onChange={(event) => setEventDraft({ ...eventDraft, q: event.target.value })} />
          </label>
          <label>
            {t('admin.status')}
            <select value={eventDraft.status} onChange={(event) => setEventDraft({ ...eventDraft, status: event.target.value })}>
              <option value="">{t('admin.any')}</option>
              <option value="processed">{t('admin.status.processed')}</option>
              <option value="ignored">{t('admin.status.ignored')}</option>
              <option value="failed">{t('admin.status.failed')}</option>
            </select>
          </label>
          <label>
            {t('admin.event')}
            <input value={eventDraft.type} placeholder="invoice.paid" onChange={(event) => setEventDraft({ ...eventDraft, type: event.target.value })} />
          </label>
          <label>
            {t('admin.from')}
            <input type="date" value={eventDraft.from} onChange={(event) => setEventDraft({ ...eventDraft, from: event.target.value })} />
          </label>
          <label>
            {t('admin.to')}
            <input type="date" value={eventDraft.to} onChange={(event) => setEventDraft({ ...eventDraft, to: event.target.value })} />
          </label>
          <div className={styles.filterActions}>
            <button type="submit" className={styles.primary}>{t('admin.apply')}</button>
            <button
              type="button"
              className={styles.ghost}
              onClick={() => {
                setEventDraft(EMPTY_EVENT_FILTERS);
                setAppliedEvents(EMPTY_EVENT_FILTERS);
                loadEvents(EMPTY_EVENT_FILTERS, 0, false);
              }}
            >
              {t('admin.clear')}
            </button>
          </div>
        </form>
      )}
      <div className={styles.toolbar}>
        <p className={styles.count}>{busy ? t('admin.loading') : t('admin.matches', { n: shownTotal })}</p>
        {view === 'payments' ? (
          <button type="button" className={styles.primary} onClick={() => { setFormError(''); setOpen(true); }}>
            {t('admin.newTx')}
          </button>
        ) : null}
      </div>
      {formError ? (
        <p className={styles.banner} role="alert">
          {formError === 'user' ? t('admin.userMissing') : t('admin.saveError')}
        </p>
      ) : null}
      {open && view === 'payments' && (
        <form className={styles.form} onSubmit={submit}>
          <label className={styles.wide}>
            {t('admin.customer')}
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label>
            {t('admin.amount')}
            <input inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} required />
          </label>
          <label>
            {t('admin.currency')}
            <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
              {CURRENCIES.map((item) => <option key={item}>{item}</option>)}
            </select>
          </label>
          <label>
            {t('admin.status')}
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {TX_STATUSES.map((item) => <option key={item} value={item}>{t(`admin.status.${item}`)}</option>)}
            </select>
          </label>
          <label className={styles.wide}>
            {t('admin.note')}
            <input value={description} maxLength={200} onChange={(event) => setDescription(event.target.value)} />
          </label>
          <div className={styles.actions}>
            <button type="button" className={styles.ghost} onClick={() => setOpen(false)}>{t('admin.cancel')}</button>
            <button type="submit" className={styles.primary} disabled={pending}>{pending ? t('admin.saving') : t('admin.save')}</button>
          </div>
        </form>
      )}
      {failed ? <p className={styles.banner}>{t('admin.loadError')}</p> : null}
      <section className={styles.panel}>
        {view === 'payments' && rows.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.when')}</th>
                <th>{t('admin.customer')}</th>
                <th>{t('admin.plan')}</th>
                <th>{t('admin.note')}</th>
                <th>{t('admin.amount')}</th>
                <th>{t('admin.status')}</th>
                <th>{t('admin.stripeId')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{when(row.createdAt, lang)}</td>
                  <td>{row.email || '—'}</td>
                  <td>{row.planName || '—'}</td>
                  <td>{row.description || '—'}</td>
                  <td>{money(row.amountCents, row.currency)}</td>
                  <td><Pill status={row.status} t={t} /></td>
                  <td className={styles.muted}>{row.stripeInvoiceId || row.stripePaymentIntentId || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {view === 'events' && events.length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.when')}</th>
                <th>{t('admin.event')}</th>
                <th>{t('admin.customer')}</th>
                <th>{t('admin.message')}</th>
                <th>{t('admin.status')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((row) => (
                <tr key={row.id}>
                  <td>{when(row.createdAt, lang)}</td>
                  <td>{row.eventType}</td>
                  <td>{row.email || '—'}</td>
                  <td>{row.message || '—'}</td>
                  <td><Pill status={row.status} t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {!busy && !failed && shown === 0 ? (
          <p className={styles.empty}>
            {(view === 'payments' ? Object.values(applied) : Object.values(appliedEvents)).some(Boolean)
              ? t('admin.noMatches')
              : t('admin.empty')}
          </p>
        ) : null}
      </section>
      {shown < shownTotal ? (
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.ghost}
            disabled={busy}
            onClick={() => (
              view === 'payments'
                ? loadPayments(applied, rows.length, true)
                : loadEvents(appliedEvents, events.length, true)
            )}
          >
            {t('admin.loadMore')}
          </button>
        </div>
      ) : null}
    </>
  );
}
